import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTenant } from '../contexts/TenantContext';
import useMenu from '../hooks/useMenu';
import { RobustPDFViewer } from './RobustPDFViewer';
import '../components/menu/menu.css';
import { apiFetch } from '../utils/apiClient';
import { getBackendBaseOrigin } from '../utils/network';

const categorie = [
    { labelKey: 'overlay.categories.bar', id: 'bar' },
    { labelKey: 'overlay.categories.ristorante', id: 'ristorante' },
    { labelKey: 'overlay.categories.caffetteria', id: 'caffetteria' },
    { labelKey: 'overlay.categories.colazione', id: 'colazione' },
    { labelKey: 'overlay.categories.pranzo', id: 'pranzo' },
    { labelKey: 'overlay.categories.tagliere', id: 'tagliere-peps' },
    { labelKey: 'overlay.categories.gelato', id: 'gelato' },
    { labelKey: 'overlay.categories.completo', id: 'completo' }
];

interface MenuOverlayProps {
    open: boolean;
    selectedCategoria: string | null;
    setSelectedCategoria: (cat: string) => void;
    onClose: () => void;
}

const MenuOverlay: React.FC<MenuOverlayProps> = ({ open, onClose, selectedCategoria, setSelectedCategoria }) => {
    const { tenantId, locations } = useTenant();
    const { t } = useTranslation();
    const { documents, loading: docsLoading, getDocumentUrl, reload } = useMenu();
    const hasMultipleLocali = (locations && locations.length > 1);
    const [step, setStep] = useState<'locali' | 'categorie' | 'menu'>(hasMultipleLocali ? 'locali' : 'categorie');
    const [selectedLocale, setSelectedLocale] = useState<string | null>(() => {
        if (hasMultipleLocali) return null;
        return (locations && locations.length > 0) ? locations[0].id : 'default';
    });
    const [numPages, setNumPages] = useState<number>(0);

    const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
        setNumPages(numPages);
    };

    useEffect(() => {
        // reload docs when tenant changes
        reload();
    }, [tenantId, reload]);

    useEffect(() => {
        if (!open) return;
        if (hasMultipleLocali) {
            setSelectedLocale(null);
            setStep('locali');
        } else {
            const defaultLocale = (locations && locations.length > 0) ? locations[0].id : 'default';
            setSelectedLocale(defaultLocale);
            setStep('categorie');
        }
    }, [open, hasMultipleLocali, locations]);

    const applyCacheBusting = (url: string, doc: any) => {
        if (!doc) return url;
        const version = doc?.updatedAt || doc?.updated_at || doc?.createdAt || doc?.created_at || doc?.id;
        if (!version) return url;
        try {
            const parsed = new URL(url);
            parsed.searchParams.set('v', String(version));
            return parsed.toString();
        } catch {
            const separator = url.includes('?') ? '&' : '?';
            return `${url}${separator}v=${encodeURIComponent(String(version))}`;
        }
    };

    const resolveDocumentInfo = (catId?: string) => {
        if (!catId) return { status: 'idle' as const, url: '', type: null, title: '', doc: null };

        const normalizedType = getDocUrlTypeNormalized(catId);
        const backendUrl = getDocumentUrl(normalizedType);
        const docsList = Array.isArray(documents) ? documents : [];
        const matchingDoc = docsList.find((doc: any) => String(doc?.docType || doc?.type || '').toUpperCase() === normalizedType);

        if (backendUrl && matchingDoc) {
            const finalUrl = backendUrl.startsWith('/')
                ? `${getBackendBaseOrigin()}${backendUrl}`
                : backendUrl;
            const versionedUrl = applyCacheBusting(finalUrl, matchingDoc);
            return {
                status: 'ready' as const,
                url: versionedUrl,
                type: normalizedType,
                title: (matchingDoc as any).name || (matchingDoc as any).title || normalizedType,
                doc: matchingDoc
            };
        }

        return {
            status: docsLoading ? 'loading' as const : 'missing' as const,
            url: '',
            type: normalizedType,
            title: normalizedType,
            doc: matchingDoc || null
        };
    };

    const getDocUrlTypeNormalized = (catId: string) => {
        const mapping: Record<string, string> = {
            'bar': 'MENU_BAR',
            'ristorante': 'MENU_RISTORANTE',
            'caffetteria': 'MENU_CAFFETTERIA',
            'colazione': 'MENU_COLAZIONE',
            'pranzo': 'MENU_PRANZO',
            'tagliere-peps': 'MENU_TAGLIERE',
            'gelato': 'MENU_GELATO',
            'completo': 'MENU_COMPLETO',
            'menu-feste': 'MENU_FESTE',
            'allergeni': 'ALLERGENI'
        };
        return mapping[catId] || catId;
    };

    const renderPdf = (info: ReturnType<typeof resolveDocumentInfo>) => {
        if (info.status === 'loading') {
            return (
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                    <p style={{ color: '#4361ee', fontSize: '1.05rem', marginBottom: '0.5rem' }}>{t('overlay.documentLoading')}</p>
                    <p style={{ color: '#999', fontSize: '0.9rem' }}>{t('overlay.documentLoadingHint')}</p>
                </div>
            );
        }

        if (info.status !== 'ready' || !info.url) {
            return (
                <div style={{ padding: '2rem', textAlign: 'center' }}>
                    <p style={{ color: '#ff6b6b', fontSize: '1.1rem', marginBottom: '1rem' }}>
                        {t('overlay.documentUnavailableTitle')}
                    </p>
                    <p style={{ color: '#999', fontSize: '0.9rem' }}>
                        {t('overlay.documentUnavailableDescription')}
                    </p>
                </div>
            );
        }

        return (
            <RobustPDFViewer
                fileUrl={info.url}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={(error: any) => console.error('PDF Load Error:', error)}
            />
        );
    };

    // Funzione per renderizzare il contenuto dell'overlay
    const renderOverlayContent = () => {
        if (selectedCategoria === 'menu-feste') {
            const info = resolveDocumentInfo('menu-feste');
            return (
                <>
                    <h2>{t('overlay.menuFeste')}</h2>
                    {renderPdf(info)}
                </>
            );
        }

        if (selectedCategoria === 'allergeni') {
            const info = resolveDocumentInfo('allergeni');
            return (
                <>
                    <h2>{t('overlay.allergensTitle')}</h2>
                    {renderPdf(info)}
                </>
            );
        }
    };

    const categoriaLabel = selectedCategoria ? (
        (() => {
            const cat = categorie.find(cat => cat.id === selectedCategoria);
            if (cat) {
                return t(cat.labelKey);
            }
            return selectedCategoria;
        })()
    ) : '';
    const selectedDocInfo = resolveDocumentInfo(selectedCategoria || undefined);

    if (!open) return null;

    // Controlla se la categoria selezionata è una categoria speciale che bypassa la navigazione
    if (selectedCategoria === 'menu-feste' || selectedCategoria === 'allergeni') {
        return (
            <div className="menu-overlay" onClick={(e) => e.stopPropagation()}>
                <button className="close-btn" onClick={onClose}>✕</button>
                {renderOverlayContent()}
            </div>
        );
    }

    return (
        <div className="menu-overlay" onClick={(e) => e.stopPropagation()}>
            <button className="close-btn" onClick={onClose}>✕</button>

            {step === 'locali' && (
                <div>
                    <h2>{t('overlay.selectLocation')}</h2>
                    {(locations || []).map(loc => (
                        <button key={loc.id} onClick={() => { setSelectedLocale(loc.id); setStep('categorie'); }}>
                            {loc.nome} <span style={{ marginLeft: 8 }}>→</span>
                        </button>
                    ))}
                </div>
            )}

            {step === 'categorie' && (
                <div className="menu-categorie-list">
                    <h2>{t('overlay.menuTypes')}</h2>
                    {categorie.map(cat => (
                        <button key={cat.id} onClick={() => { setSelectedCategoria(cat.id); setStep('menu'); }}>
                            {t(cat.labelKey)}
                        </button>
                    ))}
                </div>
            )}

            {step === 'menu' && (
                <div>
                    <button className="back-btn" onClick={() => setStep('categorie')}>← {t('menu.back')}</button>
                    <h2>{t('overlay.menuTitle', { category: categoriaLabel })}</h2>
                    {renderPdf(selectedDocInfo)}
                    {selectedCategoria !== 'allergeni' && (
                        <button
                            type="button"
                            onClick={() => setSelectedCategoria('allergeni')}
                            style={{
                                display: 'block',
                                margin: '24px auto 0',
                                fontWeight: 'bold',
                                color: '#e91e63',
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                textDecoration: 'underline'
                            }}
                        >
                            {t('overlay.viewAllergensLink')}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default MenuOverlay;